import api from 'lib/api/api';
import {
  DataResponse,
  PagedDataResponse,
  PaginationParams,
} from 'lib/api/types';
import {
  CollectionSummary,
  GetMerchantCollectionItemParams,
  GetProductCollectionItemParams,
  MerchantDetail,
  MerchantEarnedBadge,
  MerchantProduct,
  MerchantSummary,
  ProductSummary,
  ShopRedirectData,
} from 'lib/shop/types';

const shoppingApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: builder => ({
    getShopRedirectDataForMerchant: builder.query<
      ShopRedirectData,
      { merchantIdOrSlug: string; token?: string }
    >({
      query: ({ merchantIdOrSlug, token }) => {
        const params = new URLSearchParams();

        if (token) {
          params.set('token', token);
        }

        return {
          url: `/api/merchants/${merchantIdOrSlug}/shop/redirect?${params.toString()}`,
          method: 'post',
        };
      },
      transformResponse: r => (r as DataResponse<ShopRedirectData>).data,
    }),
    getShopRedirectDataForMerchantProduct: builder.query<
      ShopRedirectData,
      { merchantIdOrSlug: string; productIdOrSlug: string; token?: string }
    >({
      query: ({ merchantIdOrSlug, productIdOrSlug, token }) => {
        const params = new URLSearchParams();

        if (token) {
          params.set('token', token);
        }

        return {
          url: `/api/merchants/${merchantIdOrSlug}/shop/redirect/${productIdOrSlug}?${params.toString()}`,
          method: 'post',
        };
      },
      transformResponse: r => (r as DataResponse<ShopRedirectData>).data,
    }),

    getMerchant: builder.query<MerchantDetail, { idOrSlug: string }>({
      query: ({ idOrSlug }) => ({
        url: `/api/merchants/${idOrSlug}`,
      }),
      transformResponse: (r: DataResponse<MerchantDetail>) => r.data,
    }),

    getMerchantsByIdsOrSlugs: builder.query<
      MerchantDetail[],
      { idsOrSlugs: string[] }
    >({
      query: ({ idsOrSlugs }) => {
        // Generate search params
        const params = new URLSearchParams();
        idsOrSlugs.forEach(v => params.append('slugs[]', v));

        // Reutrn the url
        return {
          url: `/api/merchants?${params.toString()}`,
        };
      },
      transformResponse: (r: DataResponse<MerchantDetail[]>) => r.data,
    }),

    getMerchantCollection: builder.query<
      CollectionSummary,
      { categoryIdOrSlug: string }
    >({
      query: ({ categoryIdOrSlug }) => ({
        url: `/api/collections/merchants/${categoryIdOrSlug}`,
      }),
      transformResponse: (r: DataResponse<CollectionSummary>) => r.data,
    }),

    // Get the items within a collection
    getMerchantCollectionItems: builder.query<
      PagedDataResponse<MerchantSummary[]>,
      GetMerchantCollectionItemParams
    >({
      query: ({
        categoryIdOrSlug,
        indexName,
        filters,
        page,
        page_size,
        aroundLatLng,
        aroundLatLngViaIP,
      }) => {
        const searchParams = new URLSearchParams();
        if (indexName) {
          searchParams.set('index_name', indexName);
        }
        if (filters) {
          searchParams.set('filters', filters);
        }
        if (page) {
          searchParams.set('page', page.toString());
        }
        if (page_size) {
          searchParams.set('page_size', page_size.toString());
        }

        if (aroundLatLng) {
          searchParams.set('aroundLatLng', aroundLatLng);
        }

        if (aroundLatLngViaIP) {
          searchParams.set('aroundLatLngViaIP', aroundLatLngViaIP.toString());
        }

        return {
          url: `/api/collections/merchants/${categoryIdOrSlug}/items?${searchParams.toString()}`,
        };
      },
    }),

    listEarnedBadgesForMerchant: builder.query<
      MerchantEarnedBadge[],
      { merchantIdOrSlug: string }
    >({
      query: ({ merchantIdOrSlug }) => ({
        url: `/api/merchants/${merchantIdOrSlug}/badges`,
      }),
      transformResponse: (r: DataResponse<MerchantEarnedBadge[]>) => r.data,
    }),

    getProductCollection: builder.query<
      CollectionSummary,
      { categoryIdOrSlug: string }
    >({
      query: ({ categoryIdOrSlug }) => ({
        url: `/api/collections/products/${categoryIdOrSlug}`,
      }),
      transformResponse: (r: DataResponse<CollectionSummary>) => r.data,
    }),

    // Get the product items within a collection
    getProductCollectionItems: builder.query<
      PagedDataResponse<ProductSummary[]>,
      GetProductCollectionItemParams
    >({
      query: ({ categoryIdOrSlug }) => {
        return {
          url: `/api/collections/products/${categoryIdOrSlug}/items`,
        };
      },
    }),

    // Get the product items within a merchant
    getMerchantProducts: builder.query<
      MerchantProduct[],
      { merchantIdOrSlug: string } & PaginationParams
    >({
      query: ({ merchantIdOrSlug, page, page_size }) => ({
        url: `/api/merchants/${merchantIdOrSlug}/products?page=${
          page ?? 1
        }&page_size=${page_size ?? 10}`,
        method: 'get',
      }),
      transformResponse: (r: DataResponse<MerchantProduct[]>) => r.data,
    }),
  }),
});

export default shoppingApi;

export const {
  useGetShopRedirectDataForMerchantQuery,
  useGetShopRedirectDataForMerchantProductQuery,
  useGetMerchantCollectionItemsQuery,
  useGetMerchantCollectionQuery,
  useGetMerchantQuery,
  useListEarnedBadgesForMerchantQuery,
  useGetProductCollectionQuery,
  useGetProductCollectionItemsQuery,
  useGetMerchantProductsQuery,
  util: { getRunningOperationPromises },
} = shoppingApi;

export const { getMerchant } = shoppingApi.endpoints;
